import React, { useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { useSelector } from "react-redux";
import { Translation as T, useAmsSelector } from "@awe-web/shared/lib";
import { getCurrentDateTime } from "@awe-web/shared/lib";
import {
  getNewDateContainsValidCoDateTime,
  getNewIsValidCoDateTime,
  getNewDateContainsValidCiDateTime,
  getNewIsValidCiDateTime
} from "@awe-web/shared/lib";
import {
  newGetFieldLabel,
  newGetFieldPlaceholder,
} from "@awe-web/shared/lib";
import { Checkbox } from "@awe-web/shared/lib";
import { FormikStationSelection } from "./station_selection.js";
import { Row, Col } from "../../components/grid.js";
import {
  FormikInputLabel,
  Button,
  FormikDateTimeInput,
  FormikErrorMessage
} from "../../components/form.js";
import { getTimeSelectionStepSizeInMinutes } from "../../config/store.js";

import "./select_station_and_time_form.scss";

const CO_STATION_OVERLAY_HEADING = "FSD0000104";
const CI_STATION_OVERLAY_HEADING = "FSD0000156";
const CI_STATION_NOTICE = "FSD0000087";
const CO_DATE_TIME_OVERLAY_HEADING = "FSD0000331";
const CI_DATE_TIME_OVERLAY_HEADING = "FSD0000332";
const SUBMIT_LABEL = "FSD0000085";

export default function SelectStationAndTimeForm({ coStations, className, values, handleSubmit, setFieldValue, setValues }) {

  const { coStationId, ciStationId, coDateTime } = values;

  const hasCiStationId = useMemo(() => {
    return values?.ciStationId !== values?.coStationId;
  }, [values]);

  const ciStations = useMemo(() => {
    return coStations.filter(station => station.id !== coStationId);
  }, [coStationId, coStations]);

  const currentDateTime = useSelector((state) => getCurrentDateTime(state));
  const timeStepSizeInMinutes = useSelector((state) => getTimeSelectionStepSizeInMinutes(state));

  const getFieldLabel = useAmsSelector((state) => newGetFieldLabel("selectStationAndTime")(state));
  const getFieldPlaceholder = useAmsSelector((state) => newGetFieldPlaceholder("selectStationAndTime")(state));

  const isCoStationOpenAtDate = useSelector((state) => getNewDateContainsValidCoDateTime(state))(coStationId);
  const isCoStationOpenAtDateTime = useSelector((state) => getNewIsValidCoDateTime(state))(coStationId);
  const isCiStationOpenAtDate = useSelector((state) => getNewDateContainsValidCiDateTime(state))(coStationId, ciStationId, coDateTime);
  const isCiStationOpenAtDateTime = useSelector((state) => getNewIsValidCiDateTime(state))(coStationId, ciStationId, coDateTime);

  const ciStationSelectionRef = useRef();

  const handleCheckboxChange = useCallback((event) => {
    if (event.target.checked) {
      ciStationSelectionRef.current.openOverlay();
    }
    else {
      setFieldValue("ciStationId", coStationId);
    }
  }, [coStationId, setFieldValue]);

  const handleCoStationIdChange = useCallback((event) => {
    const nextCoStationId = event.target.value;

    const fValues = {
      ...values,
      coStationId: nextCoStationId,
      ciStationId: !ciStationId || ciStationId === coStationId
        ? nextCoStationId
        : ciStationId
    };

    setValues(fValues);
  }, [setValues, values, ciStationId, coStationId]);

  const combinedClassName = classnames("select-station-and-time-form", className);

  return (
    <form className={combinedClassName} onSubmit={handleSubmit}>
      <Row>
        <Col desktop={{ cols: 3 }} tablet={{ cols: 4 }} className="select-station-and-time-form__form-field">
          <div className="labeled-field">
            <FormikInputLabel name="coStationId" className="labeled-field__input-label">
              {getFieldLabel("coStationId")}
            </FormikInputLabel>
            <FormikStationSelection
              name="coStationId"
              onChange={handleCoStationIdChange}
              className="select-station-and-time-form__co-station-selection"
              placeholder={getFieldPlaceholder("coStationId")}
              overlayHeading={<T code={CO_STATION_OVERLAY_HEADING} />}
              stations={coStations}
            />
          </div>
          <FormikErrorMessage
            name="coStationId"
            className="select-station-and-time-form__co-station-id-error"
          />
        </Col>

        <Col desktop={{ cols: 3 }} tablet={{ cols: 4 }} className="select-station-and-time-form__form-field">
          <div className="labeled-field">
            <FormikInputLabel name="ciStationId" className="labeled-field__input-label">
              <label className="labeled-checkbox">
                <Checkbox
                  checked={hasCiStationId}
                  className="select-station-and-time-form__ci-station-checkbox labeled-checkbox__input"
                  onChange={handleCheckboxChange}
                />
                {getFieldLabel("ciStationId")}
              </label>
            </FormikInputLabel>
            <FormikStationSelection
              name="ciStationId"
              ref={ciStationSelectionRef}
              className="select-station-and-time-form__ci-station-selection"
              opticallyDisabled={!hasCiStationId}
              placeholder={getFieldPlaceholder("ciStationId")}
              overlayHeading={<T code={CI_STATION_OVERLAY_HEADING} />}
              stations={ciStations}
            />
          </div>
          <FormikErrorMessage name="ciStationId" />
        </Col>

        <Col desktop={{ cols: 3 }} tablet={{ cols: 4 }} className="select-station-and-time-form__form-field">
          <div className="labeled-field">
            <FormikInputLabel name="coDateTime" className="labeled-field__input-label">
              {getFieldLabel("coDateTime")}
            </FormikInputLabel>
            <FormikDateTimeInput
              name="coDateTime"
              className="select-station-and-time-form__co-date-time-input"
              placeholder={getFieldPlaceholder("coDateTime")}
              minDate={currentDateTime}
              isDateEnabled={isCoStationOpenAtDate}
              isDateTimeEnabled={isCoStationOpenAtDateTime}
              overlayHeading={<T code={CO_DATE_TIME_OVERLAY_HEADING} />}
            />
          </div>
          <FormikErrorMessage name="coDateTime" />
        </Col>

        <Col desktop={{ cols: 3 }} tablet={{ cols: 4 }} className="select-station-and-time-form__form-field">
          <div className="labeled-field">
            <FormikInputLabel name="ciDateTime" className="labeled-field__input-label">
              {getFieldLabel("ciDateTime")}
            </FormikInputLabel>
            <FormikDateTimeInput
              name="ciDateTime"
              className="select-station-and-time-form__ci-date-time-input"
              placeholder={getFieldPlaceholder("ciDateTime")}
              startDate={coDateTime}
              minDate={currentDateTime}
              timeStepSizeInMinutes={timeStepSizeInMinutes}
              isDateEnabled={isCiStationOpenAtDate}
              isDateTimeEnabled={isCiStationOpenAtDateTime}
              overlayHeading={<T code={CI_DATE_TIME_OVERLAY_HEADING} />}
            />
          </div>
          <FormikErrorMessage name="ciDateTime" />
        </Col>
      </Row>

      <p className="select-station-and-time-form__notice">
        <T code={CI_STATION_NOTICE} />
      </p>

      <Button
        submit
        continue
        primary
        className="select-station-and-time-form__submit-button"
      >
        <T code={SUBMIT_LABEL} />
      </Button>
    </form>
  );
}

SelectStationAndTimeForm.propTypes = {
  className: PropTypes.string,
  values: PropTypes.shape({
    coStationId: PropTypes.number,
    ciStationId: PropTypes.number,
    coDateTime: PropTypes.instanceOf(Date),
    ciDateTime: PropTypes.instanceOf(Date)
  }).isRequired,
  handleSubmit: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired
};
